import React, { useState, useEffect } from 'react';
import { Card, Stack, Button } from '@mui/material';
import PropTypes from 'prop-types';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/pages.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import {
  deleteDataById,
  getListData,
  toggleShowInHeaderFooter,
} from '../../services/pages.service';
import { ACTIONS } from '../../config/const.config';
import SnackbarInfo from '../common/snackbar-info.component';
import AddEditContentAppPage from './add-edit-app-content-page.component';
import ViewAppContantPage from './view-app-content-page.component';
import ConfirmPopup from '../common/confirm-popup.component';
import useAccess from '../../hooks/access.hook';
import useFilteredActions from '../../hooks/filtered-action.hook';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ListAppContentPages = ({ channelId, viewMode }) => {
  const columns = LISTING_COLUMNS;
  // eslint-disable-next-line no-nested-ternary
  const actions = !viewMode
    ? channelId === 0
      ? useFilteredActions('global_assets', LISTING_ACTIONS)
      : LISTING_ACTIONS
    : [];

  const { getSignal, isAbortError } = useAbortableRequest();

  const { hasOperationAccess } = useAccess();
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {
      search: '',
    },
    filtersCounter: 0,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleToggleHeaderFooter = (r, value, updateShowInHeader) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      id: r.id,
      value: value ? 1 : 0,
      updateShowInHeader,
    };

    toggleShowInHeaderFooter(payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: updateShowInHeader
            ? 'Show in Header updated.'
            : 'Show in Footer updated.',
        });
      })
      .catch((e) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: e.response?.data?.message,
        });
      });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteDataById(doAction.data.id)
      .then(() => {
        handleActionSuccess('Page deleted successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  useEffect(() => {
    const signal = getSignal();
    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    const filters = options.appliedFilters;
    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListData(channelId, paramsQuery, signal)
      .then((res) => {
        if (!signal.aborted) {
          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            error: false,
          });
        }
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 0, py: 2 }}
      >
        <div>
          {!viewMode &&
            ((channelId === 0 &&
              hasOperationAccess('global_assets', 'create')) ||
              channelId > 0) && (
              <Button
                color="primary"
                variant="contained"
                startIcon={<Iconify icon="ic:round-add" />}
                onClick={() => handleActionSelect(ACTIONS.CREATE.value, null)}
              >
                Create New
              </Button>
            )}
        </div>
        <div>
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            sx={{ marginRight: channelId ? 1 : 0 }}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
          handleToggleHeaderFooter={handleToggleHeaderFooter}
          disabledSwitch={
            viewMode ||
            (channelId === 0
              ? !hasOperationAccess('global_assets', 'edit')
              : !hasOperationAccess('channel_settings', 'manage'))
          }
        />
      </Card>

      {doAction.action === ACTIONS.CREATE.value && (
        <AddEditContentAppPage
          title="Create New Page"
          dataId={0}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          channelId={channelId}
        />
      )}

      {doAction.action === ACTIONS.EDIT.value && (
        <AddEditContentAppPage
          title={`Edit Page Details - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          channelId={channelId}
        />
      )}

      {doAction.action === ACTIONS.VIEW.value && (
        <ViewAppContantPage
          title={`Page Details - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title="Delete Page"
          message={`Do you want to delete ${doAction.data.title} page? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={handleDeleteData}
          successButtonText={!channelId ? 'Delete' : 'Yes'}
          cancelButtonText={!channelId ? 'Cancel' : 'No'}
          showConfirmationInput={!channelId}
          confirmationInputText="delete"
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ListAppContentPages.propTypes = {
  channelId: PropTypes.number.isRequired,
  viewMode: PropTypes.bool,
};
ListAppContentPages.defaultProps = {
  viewMode: false,
};

export default ListAppContentPages;
